/**
 * Keyboard shortcuts. Handlers never fire while the viewer is typing in a
 * field, and never swallow the browser's own modifier combinations.
 */
import { useEffect, useRef } from 'react';

export type HotkeyHandler = (event: KeyboardEvent) => void;
export type HotkeyMap = Record<string, HotkeyHandler>;

function isEditable(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  if (target.isContentEditable) return true;
  const tag = target.tagName;
  return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT';
}

/** Normalised key name: `k`, `?`, `Escape`, `mod+k`, `shift+/`. */
export function keyOf(event: KeyboardEvent): string {
  const parts: string[] = [];
  if (event.metaKey || event.ctrlKey) parts.push('mod');
  if (event.altKey) parts.push('alt');
  if (event.shiftKey && event.key.length > 1) parts.push('shift');
  parts.push(event.key.length === 1 ? event.key.toLowerCase() : event.key);
  return parts.join('+');
}

/**
 * Bind a map of shortcuts for as long as `enabled` holds. Keys that would type
 * a character are ignored while a form control has focus; `mod+…` combinations
 * still work there (so Cmd+K opens the palette from a search box).
 */
export function useHotkeys(map: HotkeyMap, enabled = true): void {
  const ref = useRef(map);
  ref.current = map;
  useEffect(() => {
    if (!enabled) return;
    const onKeyDown = (event: KeyboardEvent): void => {
      const key = keyOf(event);
      const handler = ref.current[key];
      if (!handler) return;
      const modified = key.startsWith('mod') || key === 'Escape';
      if (!modified && isEditable(event.target)) return;
      handler(event);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [enabled]);
}
